import ContentWrapper from "@/components/wrappers/ContentWrapper";
import { cssVariables } from "@/utils/utils";
import { useQueryClient } from "@tanstack/react-query";
import { Flex, Space } from "antd";
import dayjs from "dayjs";
import { t } from "i18next";
import { useState } from "react";
import { DASHBOARD_QUERY_KEYS, useGetDashboardWidgetSettings } from "../../../common/api/queries";
import { formatAgentAggregatedName } from "../../agent/utils";
import { useCurrentAgentAccount, useCurrentUser, useSelectedAccount } from "../../auth/hooks";
import { DashboardRefresh } from "../components/DashboardRefresh";
import { DashboardResponsiveGrid } from "../components/DashboardResponsiveGrid";
import { DashboardSettingsButton } from "../components/settings/DashboardSettingsButton";
import { DashboardSettingsModal } from "../components/settings/DashboardSettingsModal";
import {
  DashboardAgentEducationsWidget,
  DashboardAgentExpertisesWidget,
  DashboardCommissionsWidget,
  DashboardContactsWidget,
  DashboardContractStatisticsWidget,
  DashboardContractStatusesWidget,
  DashboardEventsWidget,
  DashboardGoalsWidget,
  DashboardLogoWidget,
  DashboardMlmStructureWidget,
  DashboardNoticesButton,
  DashboardNoticesWidget,
  DashboardPersonalDatesExtendedButton,
  DashboardPersonalDatesExtendedWidget,
  DashboardPersonalDatesWidget,
  DashboardPortfolioWidget,
  DashboardStatisticsExtendedWidget,
  DashboardStatisticsWidget,
} from "../components/widgets";
import { DashboardWidgetType } from "../enums";
import { DASHBOARD_LAYOUT_COLS, isDashboardWidgetTypeWithoutSettings, setDefaultLayoutSettings } from "../utils";

const REFRESHING_QUERY_KEYS: { type: DashboardWidgetType; queryKey: any }[] = [
  { type: DashboardWidgetType.STATISTICS, queryKey: DASHBOARD_QUERY_KEYS.getDashboardStatistics() },
  { type: DashboardWidgetType.STATISTICS_EXTENDED, queryKey: DASHBOARD_QUERY_KEYS.getDashboardExtendedStatistics() },
  { type: DashboardWidgetType.PERSONAL_DATES, queryKey: DASHBOARD_QUERY_KEYS.getDashboardPersonalDates() },
  { type: DashboardWidgetType.PERSONAL_DATES_EXTENDED, queryKey: DASHBOARD_QUERY_KEYS.getDashboardPersonalDates() },
  { type: DashboardWidgetType.AGENT_EXPERTISES, queryKey: DASHBOARD_QUERY_KEYS.getDashboardAgentExpertises() },
  { type: DashboardWidgetType.AGENT_EDUCATIONS, queryKey: DASHBOARD_QUERY_KEYS.getDashboardAgentEducations() },
  { type: DashboardWidgetType.CONTRACT_STATUSES, queryKey: DASHBOARD_QUERY_KEYS.getDashboardContractStatuses() },
  { type: DashboardWidgetType.CONTRACT_STATISTICS, queryKey: DASHBOARD_QUERY_KEYS.getDashboardContractStatistics() },
  { type: DashboardWidgetType.CONTACTS, queryKey: DASHBOARD_QUERY_KEYS.searchDashboardContacts() },
  { type: DashboardWidgetType.MLM_STRUCTURE, queryKey: DASHBOARD_QUERY_KEYS.getDashboardMlmStructure() },
  { type: DashboardWidgetType.EVENTS, queryKey: DASHBOARD_QUERY_KEYS.getDashboardEvents() },
  { type: DashboardWidgetType.COMMISSIONS, queryKey: DASHBOARD_QUERY_KEYS.getDashboardCommissions() },
  { type: DashboardWidgetType.GOALS, queryKey: DASHBOARD_QUERY_KEYS.getDashboardGoals() },
  { type: DashboardWidgetType.PORTFOLIO, queryKey: DASHBOARD_QUERY_KEYS.getDashboardPortfolio() },
];

const DASHBOARD_LAYOUT_ROW_HEIGHT = 190;
const DASHBOARD_LAYOUT_WIDTH = 1272;
const DASHBOARD_LAYOUT_WIDTH_SM = 1092;

const getDashboardWidgetByType = (key: DashboardWidgetType) => {
  let widget;

  switch (key) {
    case DashboardWidgetType.STATISTICS:
      widget = <DashboardStatisticsWidget />;
      break;
    case DashboardWidgetType.STATISTICS_EXTENDED:
      widget = <DashboardStatisticsExtendedWidget />;
      break;
    case DashboardWidgetType.PERSONAL_DATES:
      widget = <DashboardPersonalDatesWidget />;
      break;
    case DashboardWidgetType.CONTACTS:
      widget = <DashboardContactsWidget />;
      break;
    case DashboardWidgetType.LOGO:
      widget = <DashboardLogoWidget />;
      break;
    case DashboardWidgetType.GOALS:
      widget = <DashboardGoalsWidget />;
      break;
    case DashboardWidgetType.MLM_STRUCTURE:
      widget = <DashboardMlmStructureWidget />;
      break;
    case DashboardWidgetType.COMMISSIONS:
      widget = <DashboardCommissionsWidget />;
      break;
    case DashboardWidgetType.CONTRACT_STATUSES:
      widget = <DashboardContractStatusesWidget />;
      break;
    case DashboardWidgetType.CONTRACT_STATISTICS:
      widget = <DashboardContractStatisticsWidget />;
      break;
    case DashboardWidgetType.AGENT_EXPERTISES:
      widget = <DashboardAgentExpertisesWidget />;
      break;
    case DashboardWidgetType.AGENT_EDUCATIONS:
      widget = <DashboardAgentEducationsWidget />;
      break;
    case DashboardWidgetType.PORTFOLIO:
      widget = <DashboardPortfolioWidget />;
      break;
    case DashboardWidgetType.EVENTS:
      widget = <DashboardEventsWidget />;
      break;
    default:
      return null;
  }

  return <div key={key}>{widget}</div>;
};

const resolveGreetingText = () => {
  const hourOfDay = dayjs().hour();

  if (hourOfDay >= 5 && hourOfDay <= 8) {
    return t("dashboard.helpers.greetingMorning");
  } else if (hourOfDay >= 6 && hourOfDay <= 18) {
    return t("dashboard.helpers.greetingDay");
  }

  return t("dashboard.helpers.greetingEvening");
};

export const DashboardContainer = () => {
  const queryClient = useQueryClient();

  const selectedAccount = useSelectedAccount();
  const userAccount = useCurrentAgentAccount();
  const user = useCurrentUser();

  const [drawNotices, setDrawNotices] = useState(false);
  const [showPersonalDatesExtendedModal, setShowPersonalDatesExtendedModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);

  const { data } = useGetDashboardWidgetSettings();

  const dashboardWidgetsSettings = data?.filter((setting) => setting.enabled);

  const isAvailableDashboardNoticeWidget = dashboardWidgetsSettings?.some(
    (widget) => widget.widgetType === DashboardWidgetType.NOTICES,
  );

  const isAvailableDashboardPersonalDatesExtendedWidget = dashboardWidgetsSettings?.some(
    (widget) => widget.widgetType === DashboardWidgetType.PERSONAL_DATES_EXTENDED,
  );

  const gridLayoutSettings =
    dashboardWidgetsSettings?.filter((item) => !isDashboardWidgetTypeWithoutSettings(item.widgetType)) ?? [];

  return dashboardWidgetsSettings ? (
    <ContentWrapper wide key={selectedAccount.accountId} className="margin-top-large" style={{ marginBottom: 80 }}>
      <Flex justify="space-between">
        <div className="margin-bottom-small margin-top-tiny">
          <h1 style={{ fontWeight: 700, fontSize: 32, lineHeight: "38px", marginBottom: 0 }}>
            <span style={{ paddingRight: 8 }}>
              {resolveGreetingText()}, {user?.name}
            </span>
          </h1>

          <div style={{ color: cssVariables.colorGrey }}>
            {formatAgentAggregatedName(userAccount?.agent)}
            {userAccount?.representingAgent ? ` | ${formatAgentAggregatedName(userAccount?.representingAgent)}` : ""}
          </div>
        </div>

        <Space size="middle" className="margin-bottom-small margin-top-small" align="end">
          {isAvailableDashboardNoticeWidget ? (
            <DashboardNoticesButton showNoticesModal={() => setDrawNotices(true)} />
          ) : undefined}

          {isAvailableDashboardPersonalDatesExtendedWidget ? (
            <DashboardPersonalDatesExtendedButton
              showPersonalDatesExtendedModal={() => setShowPersonalDatesExtendedModal(true)}
            />
          ) : undefined}

          <DashboardRefresh
            refresh={() =>
              REFRESHING_QUERY_KEYS.filter((item) =>
                dashboardWidgetsSettings?.map((item) => item.widgetType)?.includes(item.type),
              ).forEach((item) => {
                queryClient.resetQueries({ queryKey: item.queryKey });
              })
            }
          />

          <DashboardSettingsButton showSettingsModal={() => setShowSettingsModal(true)} />
        </Space>
      </Flex>

      <DashboardResponsiveGrid
        className="layout"
        layouts={{
          lg: gridLayoutSettings
            .map((item) => item.layoutSettings ?? setDefaultLayoutSettings(item))
            .filter((layoutSettings) => !!layoutSettings),
          md: gridLayoutSettings
            .map((item) => item.layoutSettings ?? setDefaultLayoutSettings(item))
            .filter((layoutSettings) => !!layoutSettings),
          sm: gridLayoutSettings
            .map((item) => item.layoutSettings ?? setDefaultLayoutSettings(item))
            .filter((layoutSettings) => !!layoutSettings)
            .filter((layoutSettings) => layoutSettings.i !== DashboardWidgetType.LOGO),
        }}
        cols={{ lg: DASHBOARD_LAYOUT_COLS, md: DASHBOARD_LAYOUT_COLS, sm: 3 }}
        rowHeight={DASHBOARD_LAYOUT_ROW_HEIGHT}
        breakpoints={{ lg: DASHBOARD_LAYOUT_WIDTH, md: DASHBOARD_LAYOUT_WIDTH_SM + 1, sm: DASHBOARD_LAYOUT_WIDTH_SM }}
        margin={{ lg: [36, 36], md: [16, 24], sm: [36, 24] }}
        containerPadding={[0, 0]}
        dragConfig={{ enabled: false }}
        resizeConfig={{ enabled: false }}
      >
        {gridLayoutSettings.map((widget) => getDashboardWidgetByType(widget.widgetType))}
      </DashboardResponsiveGrid>

      {isAvailableDashboardNoticeWidget ? (
        <DashboardNoticesWidget isOpen={drawNotices} onCancel={() => setDrawNotices(false)} />
      ) : undefined}

      {isAvailableDashboardPersonalDatesExtendedWidget ? (
        <DashboardPersonalDatesExtendedWidget
          isOpen={showPersonalDatesExtendedModal}
          onCancel={() => setShowPersonalDatesExtendedModal(false)}
        />
      ) : undefined}

      <DashboardSettingsModal isOpen={showSettingsModal} onCancel={() => setShowSettingsModal(false)} />
    </ContentWrapper>
  ) : null;
};
